import React, { useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Users, Link2, Globe, CheckCircle2, Network, ShieldCheck, Zap, Sparkles } from 'lucide-react';
type Audience = 'personal' | 'team';
export function AudienceSwitch() {
  const [audience, setAudience] = useState<Audience>('personal');
  return <section className="w-full bg-[#FAF8F4] py-20 md:py-32 border-b border-neutral-200/70 relative overflow-hidden" data-id="element-339">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-340">
        {/* Toggle */}
        <div className="flex justify-center mb-16 md:mb-24" data-id="element-341">
          <div className="relative flex items-center p-1.5 bg-white border border-neutral-200/80 rounded-full shadow-sm" data-id="element-342">
            <div className="absolute inset-y-1.5 w-[calc(50%-6px)] bg-[#FAF8F4] border border-neutral-200/60 rounded-full shadow-sm transition-all duration-300 ease-out" style={{
            left: audience === 'personal' ? '6px' : 'calc(50%)'
          }} data-id="element-343" />
            <button onClick={() => setAudience('personal')} className={`relative z-10 flex items-center gap-2 px-6 md:px-8 py-3 rounded-full text-sm md:text-base font-semibold transition-colors duration-300 ${audience === 'personal' ? 'text-[#2B211A]' : 'text-neutral-500 hover:text-[#2B211A]'}`} data-id="element-344">
              <User size={18} className={audience === 'personal' ? 'text-[#EA580C]' : ''} data-id="element-345" />
              Giga for You
            </button>
            <button onClick={() => setAudience('team')} className={`relative z-10 flex items-center gap-2 px-6 md:px-8 py-3 rounded-full text-sm md:text-base font-semibold transition-colors duration-300 ${audience === 'team' ? 'text-[#2B211A]' : 'text-neutral-500 hover:text-[#2B211A]'}`} data-id="element-346">
              <Users size={18} className={audience === 'team' ? 'text-[#EA580C]' : ''} data-id="element-347" />
              Giga for Your Team
            </button>
          </div>
        </div>

        {/* Swappable Content */}
        <div className="relative min-h-[400px]" data-id="element-348">
          {/* Ambient background glows for the glass effect */}
          <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-[#EA580C]/10 rounded-full blur-[100px] -translate-y-1/2 pointer-events-none" data-id="element-349" />
          <div className="absolute top-1/2 right-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-[100px] -translate-y-1/2 pointer-events-none" data-id="element-350" />

          <AnimatePresence mode="wait" data-id="element-351">
            {audience === 'personal' ? <motion.div key="personal" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} transition={{
            duration: 0.4,
            ease: 'easeOut'
          }} className="flex flex-col items-center" data-id="element-352">
                <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-353">
                  <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.05] mb-6" data-id="element-354">
                    Your most <span className="italic font-light" data-id="element-355">capable</span>{' '}
                    copilot.
                  </h2>
                  <p className="text-lg md:text-xl text-neutral-500 leading-relaxed" data-id="element-356">
                    The more you ask of it, the more it does for you. Giga
                    connects to everything you use and actually finishes the
                    job.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-5xl" data-id="element-357">
                  <FeatureCard visualType="connectors" title="Plugs into your stack" description="Connect Slack, Stripe, Notion, and anything with an API. Giga reads and writes across all of them." data-id="element-358" />
                  <FeatureCard visualType="operates" title="Operates real tools" description="It runs browser sessions, queries databases, and clicks through real apps—then hands back the finished result, not a suggestion." data-id="element-359" />
                  <FeatureCard visualType="learns" title="Remembers your context" description="Every task teaches Giga your tools, data, and preferences—so the more you ask, the more it does on its own." data-id="element-360" />
                </div>
              </motion.div> : <motion.div key="team" initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -20
          }} transition={{
            duration: 0.4,
            ease: 'easeOut'
          }} className="flex flex-col items-center" data-id="element-361">
                <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-362">
                  <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.05] mb-6" data-id="element-363">
                    Your company's{' '}
                    <span className="italic font-light" data-id="element-364">shared brain.</span>
                  </h2>
                  <p className="text-lg md:text-xl text-neutral-500 leading-relaxed" data-id="element-365">
                    Every interaction maps the org in real time. Everyone's work
                    makes the whole organization smarter.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-5xl" data-id="element-366">
                  <FeatureCard visualType="map" title="Workspace Map" description="A living graph of your org. Configure where Giga looks for things, who owns what, and what's in flight." data-id="element-367" />
                  <FeatureCard visualType="permissions" title="Enterprise Permissions" description="Agents and users share the exact same permission boundaries. Nothing leaks, nothing's over-permissioned." data-id="element-368" />
                  <FeatureCard visualType="dissemination" title="Skill Dissemination" description="One user writes a prompt or knowledge artifact; all users benefit. Expertise compounds instead of staying siloed." data-id="element-369" />
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </section>;
}
function PremiumVisual({
  type
}: {
  type: string;
}) {
  // A dark, sleek container with a subtle glowing aura
  return <div className="relative w-full h-40 rounded-2xl bg-[#111] overflow-hidden mb-8 border border-neutral-800 shadow-inner group-hover:border-neutral-700 transition-colors duration-500" data-id="element-370">
      {/* Subtle animated gradient mesh background */}
      <motion.div className="absolute -inset-[100%] opacity-30" style={{
      background: 'radial-gradient(circle at 50% 50%, #EA580C 0%, transparent 50%), radial-gradient(circle at 80% 20%, #ff9a55 0%, transparent 40%)',
      filter: 'blur(40px)'
    }} animate={{
      rotate: [0, 90, 0],
      scale: [1, 1.2, 1]
    }} transition={{
      duration: 20,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-371" />

      {/* Foreground minimalistic element based on type */}
      <div className="absolute inset-0 flex items-center justify-center" data-id="element-372">
        {type === 'connectors' && <div className="relative flex items-center justify-center w-full h-full" data-id="element-373">
            {/* Central Giga hub */}
            <div className="relative z-10 w-12 h-12 rounded-xl bg-[#EA580C] flex items-center justify-center shadow-[0_0_24px_rgba(234,88,12,0.45)]" data-id="element-374">
              <span className="text-white font-bold text-sm" data-id="element-375">G</span>
            </div>
            {/* Named tool nodes wiring into the hub */}
            {[{
          slug: 'slack',
          x: '14%',
          y: '24%',
          d: 0
        }, {
          slug: 'stripe',
          x: '84%',
          y: '28%',
          d: 0.4
        }, {
          slug: 'notion',
          x: '20%',
          y: '76%',
          d: 0.8
        }, {
          slug: 'salesforce',
          x: '82%',
          y: '74%',
          d: 1.2
        }].map(node => <Fragment key={node.slug} data-id="element-376">
                {/* Connecting line with a pulse traveling toward the hub */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none" data-id="element-377">
                  <line x1={node.x} y1={node.y} x2="50%" y2="50%" stroke="rgba(234,88,12,0.35)" strokeWidth="1" strokeDasharray="3 3" data-id="element-378" />
                </svg>
                <div className="absolute -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-lg bg-white/95 flex items-center justify-center shadow-md" style={{
            left: node.x,
            top: node.y
          }} data-id="element-379">
                  <img src={`https://cdn.simpleicons.org/${node.slug}`} alt="" aria-hidden="true" className="w-4 h-4 object-contain" data-id="element-380" />
                </div>
              </Fragment>)}
          </div>}

        {type === 'operates' && <div className="w-44 rounded-lg bg-black/30 border border-white/10 p-3 backdrop-blur-sm" data-id="element-381">
            {[{
          label: 'Open browser session',
          delay: 0
        }, {
          label: 'Query the database',
          delay: 0.8
        }, {
          label: 'Update the record',
          delay: 1.6
        }].map((step, i) => <div key={step.label} className="flex items-center gap-2 py-1" data-id="element-382">
                <motion.span className="flex items-center justify-center w-4 h-4 rounded-full border border-[#EA580C]/60 text-[#EA580C]" initial={{
            opacity: 0.25
          }} animate={{
            opacity: [0.25, 1, 1]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            times: [0, 0.3, 1],
            delay: step.delay
          }} data-id="element-383">
                  <CheckCircle2 size={11} strokeWidth={3} data-id="element-384" />
                </motion.span>
                <motion.span className="text-[10px] font-mono text-white/70 whitespace-nowrap" initial={{
            opacity: 0.35
          }} animate={{
            opacity: [0.35, 1, 1]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            times: [0, 0.3, 1],
            delay: step.delay
          }} data-id="element-385">
                  {step.label}
                </motion.span>
              </div>)}
          </div>}

        {type === 'learns' && <div className="relative flex items-center justify-center w-full h-full" data-id="element-386">
            {/* Memory core */}
            <motion.div className="relative z-10 w-12 h-12 rounded-full bg-[#EA580C]/15 border border-[#EA580C]/50 flex items-center justify-center shadow-[0_0_24px_rgba(234,88,12,0.25)]" animate={{
          scale: [1, 1.08, 1]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-387">
              <Sparkles size={20} className="text-[#EA580C]" data-id="element-388" />
            </motion.div>
            {/* Context chips being absorbed into the core */}
            {[{
          label: 'tools',
          x: '12%',
          y: '30%',
          d: 0
        }, {
          label: 'data',
          x: '80%',
          y: '24%',
          d: 0.7
        }, {
          label: 'prefs',
          x: '78%',
          y: '72%',
          d: 1.4
        }].map(chip => <motion.span key={chip.label} className="absolute -translate-x-1/2 -translate-y-1/2 px-2 py-0.5 rounded-full bg-white/90 text-[9px] font-mono font-semibold text-[#2B211A]" style={{
          left: chip.x,
          top: chip.y
        }} animate={{
          left: [chip.x, '50%'],
          top: [chip.y, '50%'],
          opacity: [1, 0],
          scale: [1, 0.6]
        }} transition={{
          duration: 2.4,
          repeat: Infinity,
          delay: chip.d,
          ease: 'easeIn'
        }} data-id="element-389">
                {chip.label}
              </motion.span>)}
          </div>}

        {type === 'map' && <div className="relative w-full h-full flex items-center justify-center" style={{
        transform: 'rotateX(60deg) rotateZ(45deg) scale(1.2)'
      }} data-id="element-390">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:20px_20px]" data-id="element-391" />
            <motion.div className="absolute w-4 h-4 bg-[#EA580C] rounded-full shadow-[0_0_20px_#EA580C]" animate={{
          scale: [1, 1.2, 1]
        }} transition={{
          duration: 2,
          repeat: Infinity
        }} data-id="element-392" />
            <motion.div className="absolute w-2 h-2 bg-white/80 rounded-full" style={{
          top: '30%',
          left: '30%'
        }} animate={{
          opacity: [0.2, 1, 0.2]
        }} transition={{
          duration: 3,
          repeat: Infinity
        }} data-id="element-393" />
            <motion.div className="absolute w-2 h-2 bg-white/80 rounded-full" style={{
          top: '70%',
          left: '70%'
        }} animate={{
          opacity: [0.2, 1, 0.2]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          delay: 1
        }} data-id="element-394" />
            <svg className="absolute inset-0 w-full h-full" data-id="element-395">
              <motion.line x1="50%" y1="50%" x2="30%" y2="30%" stroke="#EA580C" strokeWidth="1" strokeOpacity="0.5" initial={{
            pathLength: 0
          }} animate={{
            pathLength: 1
          }} transition={{
            duration: 2,
            repeat: Infinity
          }} data-id="element-396" />
              <motion.line x1="50%" y1="50%" x2="70%" y2="70%" stroke="#EA580C" strokeWidth="1" strokeOpacity="0.5" initial={{
            pathLength: 0
          }} animate={{
            pathLength: 1
          }} transition={{
            duration: 2,
            repeat: Infinity,
            delay: 0.5
          }} data-id="element-397" />
            </svg>
          </div>}

        {type === 'permissions' && <div className="relative flex items-center justify-center" data-id="element-398">
            <ShieldCheck size={48} strokeWidth={1} className="text-white/20" data-id="element-399" />
            <motion.div className="absolute inset-0 overflow-hidden" style={{
          clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)'
        }} data-id="element-400">
              <motion.div className="w-full h-[2px] bg-[#EA580C] shadow-[0_0_15px_#EA580C]" animate={{
            y: [-10, 60, -10]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-401" />
            </motion.div>
            <ShieldCheck size={48} strokeWidth={1} className="text-[#EA580C] absolute inset-0 opacity-60" data-id="element-402" />
          </div>}

        {type === 'dissemination' && <div className="relative flex items-center justify-center" data-id="element-403">
            <motion.div className="absolute w-8 h-8 bg-[#EA580C] rounded-full blur-xl" animate={{
          scale: [1, 1.5, 1],
          opacity: [0.3, 0.6, 0.3]
        }} transition={{
          duration: 2,
          repeat: Infinity
        }} data-id="element-404" />
            <div className="w-6 h-6 bg-white rounded-md shadow-lg border border-white/20 z-10 flex items-center justify-center" data-id="element-405">
              <div className="w-3 h-[2px] bg-[#EA580C]/50 rounded-full" data-id="element-406" />
            </div>
            {[0, 1, 2].map(i => <motion.div key={i} className="absolute w-full h-full border border-[#EA580C]/40 rounded-full" initial={{
          scale: 0.5,
          opacity: 1
        }} animate={{
          scale: 3,
          opacity: 0
        }} transition={{
          duration: 2,
          repeat: Infinity,
          delay: i * 0.6,
          ease: 'easeOut'
        }} data-id="element-407" />)}
          </div>}
      </div>
    </div>;
}
function FeatureCard({
  visualType,
  title,
  description
}: {
  visualType: string;
  title: string;
  description: string;
}) {
  return <div className="bg-white/60 backdrop-blur-2xl rounded-[2rem] p-6 md:p-8 border border-white shadow-[0_8px_32px_rgba(43,33,26,0.04)] hover:shadow-[0_16px_48px_rgba(43,33,26,0.08)] transition-all duration-500 relative overflow-hidden group" data-id="element-408">
      <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" data-id="element-409" />

      <div className="relative z-10 flex flex-col h-full" data-id="element-410">
        <PremiumVisual type={visualType} data-id="element-411" />
        <h3 className="text-xl font-bold text-[#2B211A] mb-3" data-id="element-412">{title}</h3>
        <p className="text-neutral-500 leading-relaxed text-sm md:text-base" data-id="element-413">
          {description}
        </p>
      </div>
    </div>;
}